import { createServerFn } from "@tanstack/react-start";

/**
 * Public tag configuration for the browser.
 *
 * The GA4 measurement ID and the Google Ads conversion ID are stored as server
 * secrets (they are public by design — both ship to the browser with gtag.js —
 * but keeping them in one place avoids hardcoded IDs drifting from the ones the
 * server-side Measurement Protocol proxy uses). Read inside the handler: env
 * injection happens at call time.
 *
 * `GOOGLE_ADS_CONVERSION_LABELS` is a compact `event=label` list, e.g.
 *   purchase_trial=abc123,purchase_subscription=def456,sign_up=ghi789
 * Missing or malformed values simply leave the Ads path inert.
 */
function parseLabels(raw: string | undefined): Record<string, string> {
  if (!raw) return {};
  const out: Record<string, string> = {};
  for (const pair of raw.split(/[,\n;]/)) {
    const [key, value] = pair.split("=");
    const k = key?.trim();
    const v = value?.trim();
    if (k && v) out[k] = v;
  }
  return out;
}

/**
 * Public GA4 measurement id, hardcoded as a last-resort fallback.
 *
 * The id is public by design (it ships to every browser inside gtag.js), and
 * relying on the secret alone means one missing env binding in the deployed
 * worker silently kills ALL Google tracking — gtag.js never loads, so no
 * page_view, no conversion, no Ads. Verified failure mode in production:
 * `getTagConfig` returned `ga4MeasurementId: null` and `window.gtag` was
 * undefined on every page. The secret still wins when it is present.
 */
const GA4_MEASUREMENT_ID_FALLBACK = "G-4YFEVFN2R1";
const ADS_CONVERSION_ID_FALLBACK = "AW-18396031977";

/**
 * Ads conversion labels, hardcoded as a last-resort fallback for the same
 * reason as the GA4 id: labels are public (they ship inside every gtag
 * `conversion` call) and a missing env binding in the deployed worker silently
 * kills every Ads conversion while GA4 keeps working — verified in production,
 * where `adsLabels` reached the browser as `{}`. The secret still wins.
 */
const ADS_LABELS_FALLBACK: Record<string, string> = {
  purchase_success_quarterly: "zZhDCMDEqe0cEOnX9MNE",
  purchase_success_annual: "ezaWCNi7o-0cEOnX9MNE",
  purchase_success_trial: "mlwgCLqnqu0cEOnX9MNE",
  purchase_success_assessment: "n-FfCK_K7fEcEOnX9MNE",
  otp_requested: "iJQPCP35qu0cEOnX9MNE",
  sign_up: "TZJtCOzAoe0cEOnX9MNE",
  trial_course_cta: "ltLECPajru0cEOnX9MNE",
  quarterly_plan_cta: "Ag-1CJiAhvEcEOnX9MNE",
  annual_plan_cta: "9pkpCMPKj_EcEOnX9MNE",
};

export const getTagConfig = createServerFn({ method: "GET" }).handler(async () => {
  const adsId = process.env["GOOGLE_ADS_CONVERSION_ID"]?.trim() ?? "";
  const labels = parseLabels(process.env["GOOGLE_ADS_CONVERSION_LABELS"]);
  return {
    ga4MeasurementId:
      process.env["GA4_MEASUREMENT_ID"]?.trim() || GA4_MEASUREMENT_ID_FALLBACK,
    // Only an AW- prefixed id is a valid gtag Ads target.
    adsConversionId: adsId.startsWith("AW-") ? adsId : ADS_CONVERSION_ID_FALLBACK,
    // Merge, don't choose: a configured secret that predates a newly added
    // conversion action would otherwise hide that action's label entirely
    // (verified: the assessment thank-you page fired its event with no Ads
    // conversion because the stored secret had no assessment entry).
    // Configured values still win per event.
    adsLabels: { ...ADS_LABELS_FALLBACK, ...labels },
  };
});
